import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Highcharts from 'highcharts';
import { COMPANIES_ID } from 'projects/common/src/app/enumerables/companies-id';
import { IMachineType } from 'projects/common/src/app/interfaces/machine-type';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { firstLetterUpper, getUnits, uniqueArrayData } from 'projects/common/src/app/services/utils/utilities';
import { Subject, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { IShift } from 'src/app/modules/core/interfaces';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { DatePresentationPipe } from '../../../../core/pipes/date-presentation.pipe';
import { InputDateFormatPipe } from '../../../../core/pipes/input-date-format.pipe';
import { IProductionByMachine, IProductionDetailByMachine, IRequestProductionDetail } from '../../../interfaces/sp/productionbymachine';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';
import { ProductionModalComponent } from '../common/production-modal/production-modal.component';
import { ProductionByMachineService } from './production-by-machine.service';

@Component({
  selector: 'app-production-by-machine',
  templateUrl: './production-by-machine.component.html',
  styleUrls: ['./production-by-machine.component.scss'],
})
export class ProductionByMachineComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  public hasData = false;
  public productionData: IProductionByMachine[];
  public productionDetailData: IProductionDetailByMachine[];
  public filterShift: any;
  public filterMachine: any;

  private debouncer: Subject<any> = new Subject<any>();
  private subscription: Subscription;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    protected langService: LanguageService,
    protected modalService: NgbModal,
    protected productionService: ProductionByMachineService,
    protected formatDatePipe: InputDateFormatPipe,
    protected datePipe: DatePresentationPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.subscription = this.debouncer.pipe(throttleTime(2000)).subscribe(({ machineName, shift, date }) => {
      this.openFrontDetailModal(machineName, shift, date);
    });
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasMachineType: true,
      hasZone: true,
    };
  }

  /**
   * Function to draw the chart
   *
   * @param chartOptions ChartOptions
   */
  public drawView(chartOptions?: any): void {
    this.hasData = !!chartOptions.length;

    if (chartOptions.length) {
      this.productionData = chartOptions;
      const countProduction = this.productionData.map((m) => m.totalProductionByMachine);
      const { unit, yAxisLabel } = this.verifyIsTruck(countProduction);

      const machines = this.productionData.map((m) => m.machineName);
      this.filterMachine = machines.filter(uniqueArrayData);

      const serie = this.productionData.map((m) => m.machineName);
      const shift = this.parameterService.parameters.item('shift').value as IShift;
      const date = this.formatDatePipe.transform(this.parameterService.parameters.item('date').value);
      const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';

      const { company } = this.cacheService.local('session').user;
      const isHcm = company.id === COMPANIES_ID.HCM;

      const xAxisLabel = this.langService.message('equipments').toUpperCase();

      let shiftTooltip = '';

      if (shift.name === this.langService.message('day_detail')) {
        shiftTooltip = this.langService.message('empty');
      } else {
        shiftTooltip = this.langService.message('shift_').toUpperCase();
      }

      let unitsSingular = '';
      if (machineType.name === 'lhd') {
        unitsSingular = firstLetterUpper(this.langService.message('shelve'));
      } else if (machineType.name === 'truck') {
        unitsSingular = firstLetterUpper(this.langService.message('transfers'));
      }

      const chartTitle = '';
      let subtitleChart = '';
      subtitleChart = `${this.langService.message('company')}: ${company.name},
                  ${this.langService.message('device')}: ${this.langService.message(machineType.name)},
                  ${this.langService.message('date')}: ${date},
                  ${this.langService.message('shift_')}: ${shift.name}`;

      const paramsChart = {
        unit,
        serie,
        chartTitle,
        shift,
        date,
        shiftTooltip,
        xAxisLabel,
        filterMachine: this.filterMachine,
        machines,
        yAxisLabel,
        isHcm,
        subtitleChart,
        unitsSingular,
      };

      this.serieConfigChart(paramsChart);
      window.dispatchEvent(new Event('resize'));
    }
    this.langService.message('m017');
  }

  public getBarSerieData(shift: string) {
    const result = [];

    this.filterMachine.forEach((machine) => {
      const filteredData = this.productionData.filter((m) => {
        return m.shiftName.toUpperCase() === shift.toUpperCase() && m.machineName === machine;
      });
      const sum = filteredData.reduce((accumulator, object) => {
        return accumulator + object.totalProductionByMachine;
      }, 0);
      result.push(sum);
    });
    return result;
  }

  private getMachinme;
  /**
   *
   * Serie Confif Chart
   */
  protected serieConfigChart(params: any): void {
    const shiftDefaults = this.parameterService.parameters.item('shift-collection');
    const shiftDefault = shiftDefaults ? shiftDefaults.value : [];

    let isDetailShift = false;
    const { debouncer } = this;

    const shiftSerieA = {
      name: this.langService.message('totalloopshifta'),
      data: this.getBarSerieData('A'),
      color: '#29BCDE80',
      borderColor: '#29BCDE',
      borderWidth: 2,
      states: {
        hover: {
          color: '#29BCDE',
        },
      },
    };
    const shiftSerieB = {
      name: this.langService.message('totalloopshiftb'),
      data: this.getBarSerieData('B'),
      color: '#AFAFAF80',
      borderColor: '#AFAFAF',
      borderWidth: 2,
      states: {
        hover: {
          color: '#AFAFAF',
        },
      },
    };
    const shiftSerieC = {
      name: this.langService.message('totalloopshiftc'),
      data: this.getBarSerieData('C'),
      color: '#F7D76D80',
      borderColor: '#F7D76D',
      borderWidth: 2,
      states: {
        hover: {
          color: '#F7D76D',
        },
      },
    };
    const shiftAExist = shiftDefault?.some((s) => s.name === 'A');
    const shiftBExist = shiftDefault?.some((s) => s.name === 'B');
    const shiftCExist = shiftDefault?.some((s) => s.name === 'C');
    const shiftSelected = this.cacheService.session('shift');

    let barSerieDetailDay = [];
    let barSerie = [];

    if (shiftSelected.shiftId === -1) {
      isDetailShift = true;
      shiftDefault.forEach((s) => {
        if (shiftAExist) {
          barSerieDetailDay.push(shiftSerieA);
        }
        if (shiftBExist) {
          barSerieDetailDay.push(shiftSerieB);
        }
        if (shiftCExist) {
          barSerieDetailDay.push(shiftSerieC);
        }

        barSerieDetailDay = barSerieDetailDay.filter(uniqueArrayData);
      });
    } else {
      switch (shiftSelected.name?.toUpperCase()) {
        case 'A':
          barSerie.push(shiftSerieA);
          break;
        case 'B':
          barSerie.push(shiftSerieB);
          break;
        case 'C':
          barSerie.push(shiftSerieC);
          break;
        default:
          break;
      }

      barSerie = barSerie.filter(uniqueArrayData);
    }

    const series = isDetailShift ? barSerieDetailDay : barSerie;

    const allDataZeros = series.every((item) => item.data.every((value) => value === 0));
    if (allDataZeros) {
      this.chartWithoutData$.next(true);
    } else {
      this.chartWithoutData$.next(false);
    }

    const data = {
      chart: {
        type: 'column',
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: '#F8F8F8',
        style: {
          fontFamily: 'Open Sans',
        },
      },
      title: {
        text: params.chartTitle,
        style: {
          color: '#433e3e',
          fontWeight: '600',
          fontSize: '12px',
        },
      },
      subtitle: {
        text: params.subtitleChart,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        series: {
          borderWidth: 2,
          point: {
            events: {
              click: (event: any) => {
                const machineName = event.point.category;
                const { shift, date } = params;
                debouncer.next({ machineName, shift, date });
              },
            },
          },
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            verticalAlign() {
              return this.y === 0 ? 'bottom' : 'top';
            },
            style: {
              color: '#707070',
              textOutline: 'none',
              fontWeight: '600',
              fontSize: '11px',
              fontFamily: 'Open Sans',
            },
            y: -20,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgb(51, 51, 51, 1)',
        borderColor: '#333333',
        borderRadius: 2,
        style: {
          color: '#FFFFFF',
        },
        useHTML: true,
        formatter(): string {
          let divSymbol = '';
          divSymbol = `<div style="background-color: ${this.color};
                                width: 30px;
                                height: 12px;
                                border: 1px solid ${this.color};
                                float: left;
                                margin-top: 2px">
                            </div>`;
          return `<div style="width: 180px;
                                    font-size: 10px;
                                    color: #FFFFFF;">
                          <strong style="font-weight: 700;"> ${params.shiftTooltip}
                          ${params.shift && params.shift.name.toUpperCase()}</strong> <br>
                          <span style="font-weight: 400;">${this.x}</span> <br>
                          ${divSymbol} &nbsp;
                          <span style="font-weight: 400;">${this.y} ${
            this.y === 0 || this.y > 1 ? params.unit : params.unitsSingular
          }</span>
                        </div>`;
        },
      },
      legend: {
        layout: 'horizontal',
        squareSymbol: false,
        align: 'center',
        verticalAlign: 'top',
        margin: 30,
        useHTML: true,
        labelFormatter(): string {
          return `<div style="height: 20px; display: flex;">
                            <svg width="36" height="18" style="float: left;">
                              <rect width="30px" height="12px" x="2" y="2"
                              style="fill: ${this.color}; stroke-width: 2px !important;
                              stroke: ${this.userOptions.color} !important;" />
                            </svg>
                            <span style="font-size: 12px; font-family: Open Sans; font-weight: 400;
                            color: #433E3E; float: left; margin-left: 7px">${this.name}</span>
                          </div>`;
        },
        symbolPadding: 0.001,
        symbolWidth: 0.001,
        symbolRadius: 0.001,
        itemStyle: {
          cursor: 'default',
        },
      },
      xAxis: {
        lineWidth: 1,
        lineColor: '#707070',
        title: {
          text: params.xAxisLabel,
          style: {
            fontSize: '12px',
            color: '#707070',
            fontWeight: '600',
          },
        },
        categories: params.filterMachine,
        gridLineWidth: 1,
        labels: {
          useHTML: true,
          style: {
            fontSize: '10px',
            color: '#707070',
            fontWeight: '400',
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
      yAxis: [
        {
          title: {
            text: params.yAxisLabel.toUpperCase(),
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '600',
            },
          },
          labels: {
            style: {
              fontSize: '10px',
              color: '#707070',
              fontWeight: '400',
            },
          },
          lineWidth: 1,
          lineColor: '#707070',
        },
        {
          opposite: true,
          lineWidth: 1,
          min: 0,
          lineColor: '#707070',
          title: {
            text: '',
          },
        },
      ],
      series,
    };

    this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
      if (!this.hasData) {
        const newX = chart.plotWidth / 3 + chart.plotLeft;
        const newY = chart.plotHeight / 2 + chart.plotTop;
        chart.renderer
          .text('La solicitud no arrojó resultados', newX, newY)
          .css({
            color: '#4572A7',
            fontSize: '24px',
          })
          .add();
      }
    });
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Function to open front detail modal and load data
   */
  protected verifyIsTruck(countProduction: any[]): any {
    const isTruck = this.data.objCode === 'TRU';
    const isJumbo = this.data.objCode === 'JMB';
    let yAxisLabel = '';
    let unit = '';

    if (isTruck) {
      yAxisLabel = this.langService.message('loops');
      unit = firstLetterUpper(this.langService.message('loops'));
    } else if (isJumbo) {
      unit = firstLetterUpper(this.langService.message('metersdrill'));
      yAxisLabel = this.langService.message('metersdrill');
    } else {
      unit = firstLetterUpper(this.langService.message('shelves'));
      yAxisLabel = this.langService.message('shelves-made');
    }

    return { unit, yAxisLabel };
  }

  /**
   * Function to open front detail modal and load data
   */
  private async openFrontDetailModal(machineName: string, shift: IShift, date: string): Promise<any> {
    const companyId = this.cacheService.local('session').user.company.id;
    const dateRequest = this.parameterService.parameters.item('date').value;
    const { machineId } = this.productionData.find((m) => m.machineName === machineName);

    const machineCollection = this.parameterService.parameters.item('machine-collection').value as IMachineType[];
    const currentMachine = this.parameterService.parameters.item('machine-context').value.name as string;

    const { machineTypeId } = machineCollection.find((value) => String(value.name).toLowerCase() === String(currentMachine).toLowerCase());

    const params: IRequestProductionDetail = {
      companyId,
      inputDate: dateRequest,
      machineTypeId,
      shiftId: shift.shiftId,
      machineId,
      zoneId: this.cacheService.session('selectedZone').zoneId,
    };

    const data = await this.productionService.getProductionDetailByMachine(params);
    if (data && data.data) {
      const componentRef = this.modalService.open(ProductionModalComponent, { centered: true, windowClass: 'modal-width' });
      componentRef.componentInstance.title = machineName;
      componentRef.componentInstance.shift = shift;
      componentRef.componentInstance.date = date;
      componentRef.componentInstance.data = data.data;
      componentRef.componentInstance.machineObjCode = this.data.objCode;
    }
  }

  /**
   * On destroy component
   */
  public onDestroy(): void {
    super.ngOnDestroy();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
